import { Button, Card, Empty, Flex, Space, Spin } from 'antd';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  getCompanyActivities,
  createCompanyNote,
  createCompanyCall,
  createCompanyEmail,
  createCompanyText,
} from 'api/companies';
import { createCompanyTask } from 'api/tasks';
import { PERMISSIONS } from 'constants/permissions';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import { getData, priceTransformer } from 'utils';
import { captureException } from 'utils/errors';
import { App } from 'antd';
import AddNoteDrawer from 'containers/CustomerDetails/ActivityTab/AddNoteDrawer';
import AddCallDrawer from 'containers/CustomerDetails/ActivityTab/AddCallDrawer';
import AddTaskDrawer from 'containers/CustomerDetails/ActivityTab/AddTaskDrawer';
import ActivityTimeline from 'containers/CustomerDetails/ActivityTab/ActivityTimeline';
import { RunFlowModal } from 'containers/Flows';
import SendEmailModal from 'containers/CustomerDetails/ActivityTab/SendEmailModal';
import SendTextModal from 'containers/CustomerDetails/ActivityTab/SendTextModal';
import { StyledActivityWrapper } from 'containers/CustomerDetails/ActivityTab/styles';

function CompanySnapshotPanel({ company }) {
  return (
    <Card title="Company Snapshot" className="snapshot-card">
      <div className="snapshot-header">
        <div className="snapshot-info">
          <div className="snapshot-name">{company?.name || 'Company'}</div>
          <div className="snapshot-meta">{company?.industry || '-'}</div>
        </div>
      </div>
      <div className="snapshot-stats">
        <div className="stat">
          <span className="stat-label">Total Revenue</span>
          <span className="stat-value">{priceTransformer(company?.total_revenue ?? 0)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Contacts</span>
          <span className="stat-value">{company?.contact_count ?? 0}</span>
        </div>
      </div>
    </Card>
  );
}

const PAGE_SIZE = 20;

function ActivityTab({ company }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [callDrawerOpen, setCallDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [runFlowOpen, setRunFlowOpen] = useState(false);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const canCreateTasks = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);

  const fetchActivities = useCallback((p = 1) => {
    if (!company?.id) return;
    if (p === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    getCompanyActivities(company.id, { page: p, page_size: PAGE_SIZE })
      .then((res) => {
        const data = getData(res);
        const results = data?.data?.results ?? data?.results ?? [];
        const count = data?.data?.count ?? data?.count ?? 0;
        const items = Array.isArray(results) ? results : [];
        setActivities((prev) => (p === 1 ? items : [...prev, ...items]));
        setTotal(count);
        setPage(p);
      })
      .catch((e) => {
        captureException(e);
        message.error('Failed to load activities');
      })
      .finally(() => {
        if (p === 1) setLoading(false);
        else setLoadingMore(false);
      });
  }, [company?.id, message]);

  React.useEffect(() => {
    fetchActivities(1);
  }, [fetchActivities]);

  const handleNoteCreated = () => {
    setNoteDrawerOpen(false);
    fetchActivities(1);
    message.success('Note added');
  };

  const handleCallCreated = () => {
    setCallDrawerOpen(false);
    fetchActivities(1);
    message.success('Call logged');
  };

  const handleTaskCreated = () => {
    setTaskDrawerOpen(false);
    fetchActivities(1);
    message.success('Task created');
  };

  const handleRunFlow = () => {
    setRunFlowOpen(true);
  };

  const handleEmailSent = () => {
    setEmailModalOpen(false);
    fetchActivities(1);
    message.success('Email sent');
  };

  const handleTextSent = () => {
    setTextModalOpen(false);
    fetchActivities(1);
    message.success('Text sent');
  };

  if (!company) return null;

  return (
    <StyledActivityWrapper>
      <div className="activity-actions">
        <Space wrap>
          <Button type="primary" onClick={() => setNoteDrawerOpen(true)}>
            Add Note
          </Button>
          <Button onClick={() => setCallDrawerOpen(true)}>Add Call</Button>
          {canCreateTasks ? (
            <Button onClick={() => setTaskDrawerOpen(true)}>Create Task</Button>
          ) : null}
          <Button onClick={() => setEmailModalOpen(true)}>Email</Button>
          <Button onClick={() => setTextModalOpen(true)}>Text</Button>
          <Button onClick={handleRunFlow}>Run Flow</Button>
        </Space>
      </div>

      <div className="activity-layout">
        <div className="activity-snapshot">
          <CompanySnapshotPanel company={company} />
        </div>
        <div className="activity-timeline">
          <Card title="Activity (all contacts)" className="timeline-card">
            {loading ? (
              <Flex justify="center" align="center" style={{ minHeight: 200 }}>
                <Spin size="large" />
              </Flex>
            ) : activities.length === 0 ? (
              <Empty description="No activity yet" />
            ) : (
              <ActivityTimeline
                activities={activities}
                onLoadMore={total > page * PAGE_SIZE ? () => fetchActivities(page + 1) : null}
                loadingMore={loadingMore}
                hasReachedEnd={total > 0 && total <= page * PAGE_SIZE}
              />
            )}
          </Card>
        </div>
      </div>

      <AddNoteDrawer
        open={noteDrawerOpen}
        onClose={() => setNoteDrawerOpen(false)}
        onSubmit={(data) => createCompanyNote(company.id, data).then(handleNoteCreated)}
      />
      <AddCallDrawer
        open={callDrawerOpen}
        onClose={() => setCallDrawerOpen(false)}
        onSubmit={(data) => createCompanyCall(company.id, data).then(handleCallCreated)}
      />
      <AddTaskDrawer
        open={taskDrawerOpen}
        onClose={() => setTaskDrawerOpen(false)}
        onSubmit={(data) => createCompanyTask(company.id, data).then(handleTaskCreated)}
      />
      <SendEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={(data) => createCompanyEmail(company.id, data).then(handleEmailSent)}
      />
      <SendTextModal
        open={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        onSubmit={(data) => createCompanyText(company.id, data).then(handleTextSent)}
      />
      <RunFlowModal
        open={runFlowOpen}
        onClose={() => setRunFlowOpen(false)}
        onSuccess={() => fetchActivities(1)}
        companyId={company?.id}
      />
    </StyledActivityWrapper>
  );
}

export default ActivityTab;
